// packages/ui/src/billing/VendorPicker.tsx
//
// Shared vendor picker — modal with debounced search by name / id / email.
// Mirrors MerchantPicker behaviour so the existing UI muscle-memory
// transfers (search modal, chip-style trigger, escape-to-close).
//
// Backend: GET /api/v1/admin/vendors?q=<term>&limit=20&tab=all — search
// term matches name / legal_name / email per
// apps/api/src/modules/admin/handlers/vendors-list.ts.
//
// Usage:
//   <VendorPicker
//     value={vendorId}
//     valueLabel={vendorName}
//     onChange={(id, name) => setVendorId(id)}
//   />

import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { apiClient } from '@printway/shared/api';

export interface VendorPickerOption {
  id: string;
  name: string;
  email?: string | null;
  status?: string | null;
  /** Optional logo URL — picker renders initials chip when absent. */
  logo_url?: string | null;
}

// Phase 8.2 debug 2026-05-23 bug D — initials chip helpers (mirrors
// MerchantPicker for visual consistency). Deterministic colour from id.
const AVATAR_COLORS = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
  'bg-fuchsia-100 text-fuchsia-700',
] as const;

function pickAvatarColour(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) & 0xffff;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

function initialsFor(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '?';
  if (tokens.length === 1) return tokens[0]!.slice(0, 2).toUpperCase();
  return (tokens[0]![0]! + tokens[tokens.length - 1]![0]!).toUpperCase();
}

export interface VendorPickerProps {
  value: string | null;
  /** Caller-controlled label so the trigger shows the picked vendor name
   *  even when the modal hasn't been opened yet. */
  valueLabel?: string | null;
  onChange: (id: string | null, label: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Forwarded to the trigger button for E2E test addressing. */
  testId?: string;
  /** Tailwind extension for the trigger button. */
  className?: string;
}

interface AdminVendorListItem {
  id: string;
  name?: string | null;
  legal_name?: string | null;
  email?: string | null;
  status?: string | null;
  logo_url?: string | null;
  avatar_url?: string | null;
}

interface AdminVendorListResponse {
  items?: AdminVendorListItem[];
  vendors?: AdminVendorListItem[];
  next_cursor?: string | null;
}

interface EnvelopedAdminVendorList {
  data?: AdminVendorListResponse;
}

function unwrap(payload: unknown): AdminVendorListResponse | null {
  if (!payload || typeof payload !== 'object') return null;
  const env = payload as EnvelopedAdminVendorList;
  if (env.data && typeof env.data === 'object') return env.data;
  return payload as AdminVendorListResponse;
}

export function VendorPicker({
  value,
  valueLabel,
  onChange,
  placeholder,
  disabled,
  testId,
  className,
}: VendorPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<VendorPickerOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Debounce 300ms — keeps key-press latency low without spamming BE.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch vendors when modal opens or search changes.
  const reqIdRef = useRef(0);
  useEffect(() => {
    if (!open) return;
    const reqId = ++reqIdRef.current;
    const params = new URLSearchParams();
    params.set('limit', '20');
    params.set('tab', 'all');
    if (debouncedSearch) params.set('q', debouncedSearch);
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const c = apiClient() as unknown as {
          GET: (
            p: string,
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        };
        const { data, response } = await c.GET(
          `/api/v1/admin/vendors?${params.toString()}`,
        );
        if (reqId !== reqIdRef.current) return; // raced
        if (!response.ok) {
          setError(`HTTP ${response.status}`);
          setOptions([]);
          return;
        }
        const body = unwrap(data);
        const list = body?.items ?? body?.vendors ?? [];
        setOptions(
          list.map((v) => ({
            id: v.id,
            name:
              v.name?.trim() ||
              v.legal_name?.trim() ||
              v.email?.trim() ||
              v.id,
            email: v.email ?? null,
            status: v.status ?? null,
            logo_url: v.logo_url ?? v.avatar_url ?? null,
          })),
        );
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Network error');
        setOptions([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    })();
  }, [open, debouncedSearch]);

  const triggerLabel = useMemo(() => {
    if (value && valueLabel) return valueLabel;
    if (value) return value;
    return placeholder ?? 'Select vendor…';
  }, [value, valueLabel, placeholder]);

  function pick(opt: VendorPickerOption): void {
    onChange(opt.id, opt.name);
    setOpen(false);
  }

  function clear(e: React.MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onChange(null, null);
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid={testId ?? 'vendor-picker-trigger'}
        className={
          className ??
          'inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-pw-border bg-white px-3 text-left text-sm text-pw-text hover:bg-pw-surface disabled:cursor-not-allowed disabled:opacity-50'
        }
      >
        <span
          className={
            value ? 'truncate' : 'truncate text-pw-text-muted'
          }
        >
          {triggerLabel}
        </span>
        <span className="flex items-center gap-1">
          {value ? (
            <button
              type="button"
              onClick={clear}
              className="text-xs text-pw-text-muted hover:text-pw-text"
              aria-label="Clear vendor"
            >
              ×
            </button>
          ) : null}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-4 w-4 text-pw-text-muted"
          >
            <path
              d="M11 19a8 8 0 1 1 5.3-13.96l3.4-3.4 1.42 1.42-3.4 3.4A8 8 0 0 1 11 19zm0-2a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Pick vendor"
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6 sm:p-12"
          onClick={() => setOpen(false)}
        >
          <div
            role="document"
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          >
            <div className="border-b border-pw-border px-4 py-3 text-sm font-semibold text-pw-text">
              Pick vendor
            </div>
            <div className="border-b border-pw-border p-3">
              <input
                type="text"
                placeholder="Search by name, email, or ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                data-testid="vendor-picker-search"
                className="h-9 w-full rounded-md border border-pw-border bg-white px-3 text-sm text-pw-text outline-none focus:ring-2 focus:ring-pw-primary-strong/40"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  Loading…
                </div>
              ) : error ? (
                <div className="px-4 py-6 text-center text-sm text-red-600">
                  {error}
                </div>
              ) : options.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {debouncedSearch
                    ? 'Không tìm thấy vendor phù hợp'
                    : 'Gõ để tìm vendor theo name / email / ID'}
                </div>
              ) : (
                <ul className="divide-y divide-pw-border">
                  {options.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        onClick={() => pick(opt)}
                        data-testid={`vendor-picker-option-${opt.id}`}
                        className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-pw-surface"
                      >
                        {opt.logo_url ? (
                          <img
                            src={opt.logo_url}
                            alt=""
                            className="h-8 w-8 flex-shrink-0 rounded-full border border-pw-border bg-white object-cover"
                          />
                        ) : (
                          <span
                            aria-hidden="true"
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${pickAvatarColour(opt.id)}`}
                          >
                            {initialsFor(opt.name)}
                          </span>
                        )}
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="truncate text-sm font-medium text-pw-text">
                            {opt.name}
                          </span>
                          <span className="truncate text-xs text-pw-text-muted">
                            {opt.email ? `${opt.email} · ` : ''}
                            {opt.id}
                            {opt.status ? ` · ${opt.status}` : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-pw-border px-4 py-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 items-center rounded-md border border-pw-border bg-white px-4 text-sm font-medium text-pw-text hover:bg-pw-surface"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
